export type ActivityState = 'ouvert' | 'a-venir' | 'complet'

export interface Activity {
  id: string
  name: string
  audience: string
  place: string
  schedule: string
  price: string
  availability: string
  state: ActivityState
  opensOn?: string
  image: string
}

export interface Registration {
  id: string
  activity: string
  participant: string
  initials: string
  schedule: string
  place: string
  nextSession: string
}

export interface Facility {
  id: string
  name: string
  capacity: string
  availability: string
  price: string
  place: string
}

export interface Equipment {
  id: string
  name: string
  detail: string
  availability: string
  price: string
}

export const atnaChips = [
  'Trouver une activité',
  'Mes inscriptions',
  'Réserver un espace',
  'Louer de l’équipement',
]

/** Enfant inscrit par défaut dans le flux ATNA d'inscription. */
export const inscriptionChild = { name: 'Emma', age: '9 ans', initials: 'EL' }

/** Allergies déjà au dossier d'Emma, reprises à chaque inscription. */
export const declaredAllergies = [
  {
    id: 'amoxicilline',
    name: 'Amoxicilline',
    kind: 'Médicament',
    detail: 'Réaction cutanée · déclarée le 12 mars 2025',
  },
  {
    id: 'arachides',
    name: 'Arachides',
    kind: 'Alimentaire',
    detail: 'Auto-injecteur d’épinéphrine dans son sac',
  },
]

export const inscriptionCategories = ['Natation', 'Soccer', 'Patinage', 'Arts plastiques', 'Musique']

/** Créneau proposé par une autre famille pour un échange d'inscription. */
export interface SwapOffer {
  id: string
  registrationId: string
  day: string
  time: string
  place: string
  group: string
  offeredBy: string
  note?: string
  /** Compatibilité du créneau avec l'horaire de la famille, évaluée par ATNA. */
  fit: 'ideal' | 'possible' | 'conflit'
  fitLabel: string
}

export const swapOffers: SwapOffer[] = [
  {
    id: 'swap-1',
    registrationId: 'r-natation',
    day: 'Mercredi',
    time: '16 h 30 à 17 h 15',
    place: 'Centre récréatif',
    group: 'Natation — Junior · groupe B',
    offeredBy: 'Famille V. · 2 séances déjà faites',
    note: 'Même monitrice qu’actuellement',
    fit: 'ideal',
    fitLabel: 'Aucun conflit dans votre horaire',
  },
  {
    id: 'swap-2',
    registrationId: 'r-natation',
    day: 'Vendredi',
    time: '17 h 45 à 18 h 30',
    place: 'Centre récréatif',
    group: 'Natation — Junior · groupe D',
    offeredBy: 'Famille L. · échange direct',
    fit: 'possible',
    fitLabel: '25 min après l’école — trajet serré',
  },
  {
    id: 'swap-3',
    registrationId: 'r-natation',
    day: 'Samedi',
    time: '8 h 15 à 9 h',
    place: 'Piscine communautaire Briarwood',
    group: 'Natation — Junior · groupe A',
    offeredBy: 'Famille C. · échange direct',
    fit: 'conflit',
    fitLabel: 'Chevauche l’atelier créatif d’Emma',
  },
  {
    id: 'swap-4',
    registrationId: 'r-yoga',
    day: 'Mardi',
    time: '7 h 30 à 8 h 30',
    place: 'Chalet du parc Windermere',
    group: 'Yoga du matin · groupe 2',
    offeredBy: 'Famille R. · échange direct',
    fit: 'ideal',
    fitLabel: 'Aucun conflit dans votre horaire',
  },
  {
    id: 'swap-5',
    registrationId: 'r-yoga',
    day: 'Jeudi',
    time: '12 h à 13 h',
    place: 'Parc Centennial (extérieur)',
    group: 'Yoga du midi',
    offeredBy: 'Famille D. · échange direct',
    fit: 'possible',
    fitLabel: 'Sur l’heure du dîner',
  },
]

export interface ActivitySlot {
  id: string
  category: string
  name: string
  day: string
  time: string
  place: string
  audience: string
  price: string
  spots: string
  /** Créneau mis en avant par ATNA (aucun conflit à l'horaire). */
  recommended?: boolean
  note?: string
}

export const availableSlots: ActivitySlot[] = [
  {
    id: 'natation-mer',
    category: 'Natation',
    name: 'Natation — Niveau 3',
    day: 'Mercredi',
    time: '16 h 30 à 17 h 15',
    place: 'Centre récréatif',
    audience: '8 à 10 ans',
    price: '68 $ · 10 séances',
    spots: '4 places restantes',
    recommended: true,
    note: 'Aucun conflit avec l’horaire d’Emma',
  },
  {
    id: 'natation-sam',
    category: 'Natation',
    name: 'Natation — Niveau 3',
    day: 'Samedi',
    time: '10 h à 10 h 45',
    place: 'Centre récréatif',
    audience: '8 à 10 ans',
    price: '68 $ · 10 séances',
    spots: '1 place restante',
  },
  {
    id: 'natation-ven',
    category: 'Natation',
    name: 'Natation — Niveau 3',
    day: 'Vendredi',
    time: '18 h à 18 h 45',
    place: 'Piscine communautaire Briarwood',
    audience: '8 à 11 ans',
    price: '68 $ · 10 séances',
    spots: '6 places restantes',
  },
  {
    id: 'soccer-mar',
    category: 'Soccer',
    name: 'Soccer récréatif',
    day: 'Mardi',
    time: '17 h 30 à 18 h 45',
    place: 'Parc Shannon',
    audience: '8 à 10 ans',
    price: '95 $ · saison',
    spots: '8 places restantes',
    recommended: true,
    note: 'À 5 minutes de votre adresse',
  },
  {
    id: 'soccer-sam',
    category: 'Soccer',
    name: 'Soccer récréatif',
    day: 'Samedi',
    time: '9 h à 10 h 15',
    place: 'Parc Rockhill',
    audience: '8 à 10 ans',
    price: '95 $ · saison',
    spots: '3 places restantes',
  },
  {
    id: 'patin-jeu',
    category: 'Patinage',
    name: 'Patinage — Initiation',
    day: 'Jeudi',
    time: '17 h à 18 h',
    place: 'Centre récréatif',
    audience: '7 à 10 ans',
    price: '72 $ · 12 séances',
    spots: '5 places restantes',
    recommended: true,
    note: 'Équipement fourni par la Ville',
  },
  {
    id: 'arts-mer',
    category: 'Arts plastiques',
    name: 'Atelier d’arts plastiques',
    day: 'Mercredi',
    time: '18 h à 19 h 15',
    place: 'Centre culturel Beaconsfield',
    audience: '8 à 12 ans',
    price: '58 $ · 8 ateliers',
    spots: '7 places restantes',
    recommended: true,
    note: 'Matériel inclus',
  },
  {
    id: 'musique-lun',
    category: 'Musique',
    name: 'Éveil musical — guitare',
    day: 'Lundi',
    time: '17 h 15 à 18 h',
    place: 'Bibliothèque de Beaconsfield',
    audience: '8 à 12 ans',
    price: '110 $ · 10 cours',
    spots: '2 places restantes',
    recommended: true,
    note: 'Instrument prêté pour la session',
  },
]

export const activities: Activity[] = [
  {
    id: 'natation-junior',
    name: 'Natation — Junior',
    audience: '8 à 12 ans',
    place: 'Centre récréatif',
    schedule: 'Samedi · 10 h',
    price: '85 $',
    availability: '12 places disponibles',
    state: 'ouvert',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/09aab17e-70f7-466f-a4f5-58c6bbdb4e7c.jpg',
  },
  {
    id: 'pickleball',
    name: 'Pickleball libre',
    audience: 'Adultes',
    place: 'Parc Windermere',
    schedule: 'Mardi et jeudi · 18 h 30',
    price: '40 $ / saison',
    availability: 'Inscriptions à venir',
    state: 'a-venir',
    opensOn: 'lundi 12 octobre · 9 h',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/69ca7f97-304e-4e63-968b-9130fae01a37.jpg',
  },
  {
    id: 'mise-en-forme',
    name: 'Mise en forme 50 ans et plus',
    audience: '50 ans et plus',
    place: 'Centre récréatif',
    schedule: 'Lundi et mercredi · 9 h 30',
    price: '60 $',
    availability: 'Complet',
    state: 'complet',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1cf684cf-5dd0-4a3c-a0a5-5144e34e195a.jpg',
  },
  {
    id: 'atelier-creatif',
    name: 'Atelier créatif ado',
    audience: '13 à 17 ans',
    place: 'Bibliothèque de Beaconsfield',
    schedule: 'Vendredi · 17 h',
    price: '45 $',
    availability: '5 places disponibles',
    state: 'ouvert',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a2858b27-8254-4ffc-98f5-bf2103bc4d3d.jpg',
  },
]

/** Inscription validée par la Ville, en attente du paiement. */
export const readiness = {
  activity: 'Natation — Junior',
  participant: 'Emma',
  session: 'Mercredi · 16 h 30 · Centre récréatif',
  reference: '#INS-2026-3312',
  amount: '68,00 $',
  payBefore: 'vendredi 16 octobre, 23 h 59',
  checks: [
    { id: 'carte', label: 'Carte numérique', ok: true },
    { id: 'residence', label: 'Statut de résidence', ok: true },
    { id: 'participant', label: 'Profil du participant', ok: true },
    { id: 'place', label: 'Place réservée', ok: true },
  ],
}

/** Carte enregistrée au dossier, proposée par défaut au paiement en ligne. */
export const savedCard = { brand: 'Visa', last4: '4127', expiry: '09/29' }

/** Étapes du paiement en argent comptant, au comptoir. */
export const cashSteps = [
  {
    title: 'Générez votre code de paiement',
    detail: 'Un code unique est rattaché à l’inscription d’Emma. Présentez-le au comptoir.',
  },
  {
    title: 'Présentez-vous à un point de service',
    detail:
      'Hôtel de ville (303, boulevard Beaconsfield) ou Centre récréatif · du lundi au jeudi, 8 h à 16 h 45 ; vendredi, 8 h à 12 h.',
  },
  {
    title: 'Payez en argent comptant',
    detail: 'Montant exact recommandé. Le personnel encaisse et confirme l’inscription sur place.',
  },
  {
    title: 'Recevez votre reçu',
    detail: 'Le reçu est déposé dans Mon profil → Mes factures et la place est définitivement confirmée.',
  },
]

export const participants = [
  { id: 'marianne', name: 'Marianne', initials: 'MC', detail: 'Titulaire' },
  { id: 'emma', name: 'Emma', initials: 'EL', detail: '10 ans' },
  { id: 'eric', name: 'Éric', initials: 'ÉL', detail: 'Conjoint' },
]

export const registrations: Registration[] = [
  {
    id: 'r-natation',
    activity: 'Natation — Junior',
    participant: 'Emma',
    initials: 'EL',
    schedule: 'Samedi · 10 h',
    place: 'Centre récréatif',
    nextSession: 'Prochaine séance : samedi 10 octobre',
  },
  {
    id: 'r-yoga',
    activity: 'Yoga du matin',
    participant: 'Marianne',
    initials: 'MC',
    schedule: 'Mercredi · 7 h 30',
    place: 'Chalet du parc Windermere',
    nextSession: 'Prochaine séance : mercredi 7 octobre',
  },
]

export const followUps = [
  'Vos inscriptions sont maintenant gérées ici : plus besoin d’un compte séparé sur la plateforme externe.',
  'Le cours de natation d’Emma est déplacé au petit bassin ce samedi.',
  'Les inscriptions au niveau suivant ouvrent lundi — Emma y sera admissible.',
]

export const facilities: Facility[] = [
  {
    id: 'salle-communautaire',
    name: 'Chalet du parc Windermere',
    capacity: '20 à 40 personnes',
    availability: 'Samedi 13 h à 17 h',
    price: '75 $ / 4 h',
    place: '303, rue Sherbrooke',
  },
  {
    id: 'gymnase',
    name: 'Gymnase',
    capacity: 'Sports d’équipe',
    availability: 'Samedi 9 h à 12 h',
    price: '45 $ / h',
    place: 'Centre récréatif, 1974, City Lane',
  },
  {
    id: 'patinoire',
    name: 'Patinoire intérieure',
    capacity: 'Glace complète',
    availability: 'Dimanche 20 h à 21 h',
    price: '120 $ / h',
    place: 'Centre récréatif, 1974, City Lane',
  },
]

export const equipment: Equipment[] = [
  { id: 'raquettes', name: 'Raquettes à neige', detail: '4 paires · adultes', availability: 'Dès le 15 décembre', price: '10 $ / jour' },
  { id: 'patins', name: 'Patins', detail: 'Tailles 1 à 12', availability: 'Disponible', price: 'Gratuit' },
  { id: 'communautaire', name: 'Matériel communautaire', detail: 'Tables, chapiteaux, chaises', availability: 'Sur réservation', price: 'Selon le matériel' },
]

/** Créneau réservable d'un espace municipal. */
export interface FacilitySlot {
  id: string
  facilityId: string
  day: string
  time: string
  duration: string
  amount: string
  left: string
}

export const facilitySlots: FacilitySlot[] = [
  {
    id: 'salle-sam-13',
    facilityId: 'salle-communautaire',
    day: 'Samedi 17 octobre',
    time: '13 h à 17 h',
    duration: '4 h',
    amount: '75,00 $',
    left: 'Dernier bloc de la journée',
  },
  {
    id: 'salle-dim-10',
    facilityId: 'salle-communautaire',
    day: 'Dimanche 18 octobre',
    time: '10 h à 14 h',
    duration: '4 h',
    amount: '75,00 $',
    left: 'Disponible',
  },
  {
    id: 'salle-sam-26',
    facilityId: 'salle-communautaire',
    day: 'Samedi 24 octobre',
    time: '18 h à 22 h',
    duration: '4 h',
    amount: '95,00 $',
    left: 'Tarif de soirée',
  },
  {
    id: 'gym-sam-9',
    facilityId: 'gymnase',
    day: 'Samedi 17 octobre',
    time: '9 h à 11 h',
    duration: '2 h',
    amount: '90,00 $',
    left: 'Disponible',
  },
  {
    id: 'gym-sam-11',
    facilityId: 'gymnase',
    day: 'Samedi 17 octobre',
    time: '11 h à 12 h',
    duration: '1 h',
    amount: '45,00 $',
    left: 'Dernière heure libre',
  },
  {
    id: 'gym-mer-19',
    facilityId: 'gymnase',
    day: 'Mercredi 21 octobre',
    time: '19 h à 21 h',
    duration: '2 h',
    amount: '90,00 $',
    left: 'Disponible',
  },
  {
    id: 'glace-dim-20',
    facilityId: 'patinoire',
    day: 'Dimanche 18 octobre',
    time: '20 h à 21 h',
    duration: '1 h',
    amount: '120,00 $',
    left: 'Glace complète',
  },
  {
    id: 'glace-sam-22',
    facilityId: 'patinoire',
    day: 'Samedi 24 octobre',
    time: '22 h à 23 h',
    duration: '1 h',
    amount: '120,00 $',
    left: 'Dernier bloc du week-end',
  },
]

/** Conditions rappelées avant de confirmer la location d'un espace. */
export const facilityConditions = [
  'Présentez votre carte numérique à l’arrivée : elle déverrouille l’accès au local réservé.',
  'Annulation sans frais jusqu’à 48 h avant le créneau.',
  'Le local doit être remis dans son état initial à la fin du bloc réservé.',
]

/** Point et plage de cueillette d'un équipement. */
export interface PickupWindow {
  id: string
  equipmentId: string
  place: string
  day: string
  time: string
  note?: string
}

export const pickupWindows: PickupWindow[] = [
  {
    id: 'raquettes-christmas',
    equipmentId: 'raquettes',
    place: 'Chalet du parc Christmas',
    day: 'Samedi 19 décembre',
    time: '9 h à 10 h',
    note: 'Retour le lendemain avant 17 h',
  },
  {
    id: 'raquettes-recreatif',
    equipmentId: 'raquettes',
    place: 'Centre récréatif',
    day: 'Dimanche 20 décembre',
    time: '10 h à 11 h',
  },
  {
    id: 'patins-recreatif',
    equipmentId: 'patins',
    place: 'Centre récréatif',
    day: 'Samedi 17 octobre',
    time: '13 h à 14 h',
    note: 'Prêt gratuit pour les résidents',
  },
  {
    id: 'patins-beacon-hill',
    equipmentId: 'patins',
    place: 'Chalet du parc Beacon Hill',
    day: 'Dimanche 18 octobre',
    time: '11 h à 12 h',
  },
  {
    id: 'communautaire-travaux',
    equipmentId: 'communautaire',
    place: 'Travaux publics (300, chemin Beaurepaire)',
    day: 'Vendredi 16 octobre',
    time: '8 h 30 à 11 h',
    note: 'Transport à la charge du citoyen',
  },
  {
    id: 'communautaire-recreatif',
    equipmentId: 'communautaire',
    place: 'Centre récréatif',
    day: 'Vendredi 16 octobre',
    time: '15 h à 16 h 30',
  },
]
